import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { WORK_CHAT_RUN_SPEED_QUERY_KEY } from '../../contracts';
import type { WorkRunSpeed } from '../../contracts/project-run-flags';
import { getWorkChatRunSpeed, setWorkChatRunSpeed } from '../api/settings';
import { useIsAuthenticated } from '../injected-auth';

export { WORK_CHAT_RUN_SPEED_QUERY_KEY };

/**
 * Seeds a new chat's composer with the Fast/Normal speed the user last picked.
 * `null` is a real state — never picked — and each surface falls back to Normal
 * rather than being moved onto a speed nobody chose. Writes are best-effort:
 * this only remembers a preference, it never decides a live run's flags.
 *
 * API: GET/PATCH /api/v2/ml-scribe/settings (key `work-chat_run_speed`)
 */
export function useWorkRunSpeedPreference() {
  const isAuthenticated = useIsAuthenticated();
  const queryClient = useQueryClient();

  const { data } = useQuery<WorkRunSpeed | null, Error>({
    queryKey: [WORK_CHAT_RUN_SPEED_QUERY_KEY],
    enabled: isAuthenticated,
    queryFn: getWorkChatRunSpeed,
  });
  const {
    mutate,
    isPending,
    variables: inFlightSpeed,
  } = useMutation<WorkRunSpeed | null, Error, WorkRunSpeed>({
    mutationFn: setWorkChatRunSpeed,
    // A concrete echo wins: a locked setting can resolve a different speed
    // than the one sent. A response that omits the key reads as `null` and
    // must not blank the cache, so that case keeps the value sent.
    onSuccess: (response, speed) => {
      queryClient.setQueryData([WORK_CHAT_RUN_SPEED_QUERY_KEY], response ?? speed);
    },
  });
  const persistedSpeed = data ?? null;

  const rememberSpeed = useCallback(
    (next: WorkRunSpeed) => {
      if (!isAuthenticated) return;
      if (next === (isPending ? inFlightSpeed : persistedSpeed)) return;
      mutate(next);
    },
    [inFlightSpeed, isAuthenticated, isPending, mutate, persistedSpeed]
  );

  return { persistedSpeed, rememberSpeed };
}
